/**
 * Ensures a Firestore user document exists for the given Firebase user.
 * Creates the document if it does not exist.
 */
import { User } from "firebase/auth";
import { GoogleAuthProvider, sendSignInLinkToEmail, signInWithPopup, UserCredential } from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { auth, db, provider, redirectURL } from "./firebase-service";
import * as Sentry from '@sentry/browser';

const actionCodeSettings = {
  url: redirectURL,
  handleCodeInApp: true
};

// Authentication error categories for better analysis
enum AuthErrorCategory {
  INVALID_EMAIL = 'invalid_email',
  EMAIL_IN_USE = 'email_in_use',
  POPUP_BLOCKED = 'popup_blocked',
  POPUP_CLOSED = 'popup_closed',
  NETWORK = 'network',
  CANCELLED = 'cancelled',
  PERMISSION = 'permission',
  TOKEN_EXPIRED = 'token_expired',
  ACCOUNT_EXISTS = 'account_exists',
  FIRESTORE = 'firestore',
  UNKNOWN = 'unknown'
}

/**
 * Sends an email link for authentication and tracks performance/errors
 */
const loginWithEmailLink = async (email: string, redirectUrl?: string) => {
  // Start performance monitoring
  const startTime = performance.now();

  // Create a transaction ID for tracking this authentication attempt
  const transactionId = `email_auth_${Date.now()}`;

  // Always include email as a query param in the magic link for robust fallback
  const baseUrl = redirectUrl || actionCodeSettings.url;
  const urlWithEmail = baseUrl.includes('?')
    ? `${baseUrl}&email=${encodeURIComponent(email)}`
    : `${baseUrl}?email=${encodeURIComponent(email)}`;

  const settings = {
    ...actionCodeSettings,
    url: urlWithEmail,
  };

  // Add breadcrumb to track authentication attempt
  Sentry.addBreadcrumb({
    category: 'auth',
    message: `Starting email link authentication for: ${email}`,
    level: 'info',
    data: {
      email: email,
      redirectURL: settings.url,
      transactionId
    }
  });

  try {
    await sendSignInLinkToEmail(auth, email, settings);

    // Save the email locally
    localStorage.setItem("emailForSignIn", email);

    // Track successful email link sending in Sentry
    const duration = performance.now() - startTime;
    Sentry.captureMessage('Email authentication link sent successfully', {
      level: 'info',
      tags: {
        operation: 'loginWithEmailLink',
        status: 'success',
        transactionId
      },
      extra: {
        email,
        timing: {
          totalMs: duration,
        },
        redirectURL: settings.url
      }
    });

    return true;
  } catch (error: any) {
    const errorCode = error.code;
    const errorMessage = error.message;
    console.error(error);
    console.error(errorCode + ": " + errorMessage);

    // Determine error category
    const errorCategory = determineEmailAuthErrorCategory(errorCode);

    // Track error in Sentry with detailed context
    Sentry.captureException(error, {
      tags: {
        operation: 'loginWithEmailLink',
        status: 'error',
        errorCategory,
        errorCode,
        transactionId
      },
      extra: {
        email,
        timing: {
          totalMs: performance.now() - startTime
        },
        redirectURL: settings.url,
        errorDetails: {
          code: errorCode,
          message: errorMessage
        }
      }
    });

    return false;
  }
}

/**
 * Authenticates with Google and tracks performance/errors
 */
const loginWithGoogle = async () => {
  // Start performance monitoring
  const startTime = performance.now();

  // Create a transaction ID for tracking this authentication attempt
  const transactionId = `google_auth_${Date.now()}`;

  // Add breadcrumb to track authentication attempt
  Sentry.addBreadcrumb({
    category: 'auth',
    message: 'Starting Google authentication',
    level: 'info',
    data: {
      provider: 'Google',
      transactionId
    }
  });

  try {
    // Track popup timing
    const popupStartTime = performance.now();

    try {
      const result = await signInWithPopup(auth, provider);

      // Calculate popup duration
      const popupDuration = performance.now() - popupStartTime;

      // Add breadcrumb for successful authentication
      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'Google authentication successful, saving to Firestore',
        level: 'info',
        data: {
          userId: result.user.uid,
          email: result.user.email,
          timing: {
            popupMs: popupDuration
          },
          transactionId
        }
      });

      // Track Firestore operation timing
      const firestoreStartTime = performance.now();

      // Save user data to Firestore
      await saveSessionToFirestore(result, transactionId);
      await ensureFirestoreUser(result.user);

      // Calculate Firestore duration
      const firestoreDuration = performance.now() - firestoreStartTime;

      // Track overall success
      const totalDuration = performance.now() - startTime;
      Sentry.captureMessage('Google authentication completed successfully', {
        level: 'info',
        tags: {
          operation: 'loginWithGoogle',
          status: 'success',
          transactionId
        },
        extra: {
          user: {
            email: result.user.email,
            id: result.user.uid
          },
          timing: {
            totalMs: totalDuration,
            popupMs: popupDuration,
            firestoreMs: firestoreDuration
          }
        }
      });

      return true;
    } catch (error: any) {
      // Handle Errors here.
      const errorCode = error.code;
      const errorMessage = error.message;
      // The email of the user's account used.
      const email = error.customData?.email;
      // The AuthCredential type that was used.
      const credential = GoogleAuthProvider.credentialFromError(error);

      // Calculate popup duration if it occurred during popup
      const popupDuration = performance.now() - popupStartTime;

      // Determine error category
      const errorCategory = determineGoogleAuthErrorCategory(errorCode);

      // Track error in Sentry with detailed context
      Sentry.captureException(error, {
        tags: {
          operation: 'loginWithGoogle',
          status: 'error',
          errorCategory,
          errorCode,
          transactionId
        },
        extra: {
          email,
          credential: credential ? 'present' : 'missing',
          timing: {
            totalMs: performance.now() - startTime,
            popupMs: popupDuration
          },
          errorDetails: {
            code: errorCode,
            message: errorMessage
          }
        }
      });

      // Log to console as well
      console.error(`Error during Google login: ${errorCode} - ${errorMessage}`, { email, credential });
      return false;
    }
  } catch (outerError: any) {
    // Handle any unexpected errors outside the main try/catch
    Sentry.captureException(outerError, {
      tags: {
        operation: 'loginWithGoogle',
        status: 'unexpected_error',
        transactionId
      },
      extra: {
        timing: {
          totalMs: performance.now() - startTime
        }
      }
    });

    console.error("Unexpected error during Google login:", outerError);
    return false;
  }
}


export async function ensureFirestoreUser(user: User) {
  if (!user?.uid) {
    console.error('[Firestore] No UID found for user:', user);
    return;
  }

  if (!user?.email) {
    console.error('[Firestore] No email found for user:', user);
    return;
  }

  const userDocRef = doc(db, "users", user.uid);
  try {
    const docSnapshot = await getDoc(userDocRef);
    if (!docSnapshot.exists()) {
      const userData = {
        email: user.email,
        name: user.displayName || "SaaS User",
        uid: user.uid,
        createdAt: new Date(),
        customerID: "",
        postSignupComplete: false,
        company: "",
        role: "",
        managed: false,

      };
      console.log('[Firestore] Creating user document:', userData);
      await setDoc(userDocRef, userData);
      console.log('[Firestore] User document created successfully for UID:', user.uid);
    } else {
      console.log('[Firestore] User document already exists for UID:', user.uid);
    }
  } catch (err: any) {
    console.error('[Firestore] Error creating or checking user document:', err);
    // Do not show any alert, just log to console
  }
}

/**
 * Saves user session data to Firestore and tracks performance/errors
 */
const saveSessionToFirestore = async (cred: UserCredential, transactionId?: string) => {
  const startTime = performance.now();
  // Debug log: function called
  console.log('[Firestore] saveSessionToFirestore called for UID:', cred.user.uid);

  if (cred.user.uid === "") {
    const error = new Error("User ID in credential is empty!");
    console.error(error);

    // Debug log: credential
    console.log('[Firestore] Credential:', cred);
    // Track error in Sentry
    Sentry.captureException(error, {
      tags: {
        operation: 'saveSessionToFirestore',
        status: 'error',
        errorCategory: 'invalid_credential',
        transactionId
      },
      extra: {
        userInfo: {
          email: cred.user.email,
          displayName: cred.user.displayName
        },
        timing: {
          totalMs: performance.now() - startTime
        }
      }
    });

    return false;
  }

  const userDocRef = doc(db, 'users', cred.user.uid);
  let displayName = cred.user.displayName;
  let isNewUser = false;

  if (displayName == null) {
    displayName = "SaaS User";
  }

  console.log('[Firestore] userDocRef path:', userDocRef.path);
  console.log('[Firestore] displayName was null, set to default:', displayName);
  // Save user to user collection
  try {
    // Add breadcrumb for Firestore check
    Sentry.addBreadcrumb({
      category: 'firestore',
      message: 'Checking if user exists in Firestore',
      level: 'info',
      data: {
        userId: cred.user.uid,
        transactionId
      }
    });

    // Check if user exists
    const docCheckStartTime = performance.now();
    const docSnapshot = await getDoc(userDocRef);
    const docCheckDuration = performance.now() - docCheckStartTime;

    isNewUser = !docSnapshot.exists();
    // Debug log: user existence
    console.log(`[Firestore] User exists: ${docSnapshot.exists()} (UID: ${cred.user.uid})`);
    console.log('[Firestore] Credential:', cred);


    if (isNewUser) {
      // Add breadcrumb for new user creation
      Sentry.addBreadcrumb({
        category: 'firestore',
        message: 'Creating new user in Firestore',
        level: 'info',
        data: {
          userId: cred.user.uid,
          email: cred.user.email,
          transactionId
        }
      });


      // Set initial user data
      const userData = {
        email: cred.user.email,
        name: displayName,
        uid: cred.user.uid,
        createdAt: new Date(),
        customerID: "",
        postSignupComplete: false,
        company: "",
        role: "",
        managed: false,
        hasHadPremiumTrial: false
      };
      // Helper to read postSignupComplete from a profile document

      // Debug logging for Firestore write (always log)
      // eslint-disable-next-line no-console
      console.log(`[Firestore] Creating user:`, userData);

      // Track write operation timing
      const writeStartTime = performance.now();
      let writeError = null;
      try {
        await setDoc(userDocRef, userData);
        console.log('[Firestore] setDoc success:', userData);
      } catch (err) {
        writeError = err;
        console.error('[Firestore] setDoc error:', err);
        if (err && (err as any).message) {
          alert('Firestore write failed: ' + (err as any).message);
        } else {
          alert('Firestore write failed: Unknown error');
        }
      }
      const writeDuration = performance.now() - writeStartTime;

      // Calculate total operation time
      const totalDuration = performance.now() - startTime;
      try {
        await setDoc(userDocRef, userData);
        console.log('[Firestore] setDoc success:', userData);
      } catch (err) {
        console.error('[Firestore] setDoc error:', err);
      }

      // Track new user creation in Sentry
      Sentry.captureMessage('New user created in Firestore', {
        level: 'info',
        tags: {
          operation: 'saveSessionToFirestore',
          status: 'success',
          userType: 'new',
          transactionId
        },
        extra: {
          user: {
            id: cred.user.uid,
            email: cred.user.email
          },
          timing: {
            totalMs: totalDuration,
            docCheckMs: docCheckDuration,
            writeMs: writeDuration
          },
          isEmployee: cred.user.email?.endsWith("@blueskeye.com")
        }
      });
    } else {
      // Calculate total operation time
      const totalDuration = performance.now() - startTime;

      // Track existing user login in Sentry
      Sentry.captureMessage('Existing user logged in', {
        level: 'info',
        tags: {
          operation: 'saveSessionToFirestore',
          status: 'success',
          userType: 'existing',
          transactionId
        },
        extra: {
          user: {
            id: cred.user.uid,
            email: cred.user.email
          },
          timing: {
            totalMs: totalDuration,
            docCheckMs: docCheckDuration
          }
        }
      });
    }

    return true;
  } catch (error: any) {
    console.error("Error checking or adding user to Firestore:", error);

    // Track Firestore error in Sentry
    Sentry.captureException(error, {
      tags: {
        operation: 'saveSessionToFirestore',
        status: 'error',
        errorCategory: AuthErrorCategory.FIRESTORE,
        transactionId,
        isNewUser: isNewUser ? 'true' : 'false'
      },
      extra: {
        user: {
          id: cred.user.uid,
          email: cred.user.email,
          displayName: displayName
        },
        timing: {
          totalMs: performance.now() - startTime
        },
        firestoreAction: isNewUser ? 'create' : 'read'
      }
    });

    return false;
  }
}

// Helper functions to categorize authentication errors

function determineEmailAuthErrorCategory(errorCode: string): AuthErrorCategory {
  // Firebase email link error codes
  switch (errorCode) {
    case 'auth/invalid-email':
    case 'auth/missing-email':
      return AuthErrorCategory.INVALID_EMAIL;
    case 'auth/email-already-in-use':
      return AuthErrorCategory.EMAIL_IN_USE;
    case 'auth/network-request-failed':
      return AuthErrorCategory.NETWORK;
    case 'auth/unauthorized-domain':
    case 'auth/operation-not-allowed':
      return AuthErrorCategory.PERMISSION;
    default:
      return AuthErrorCategory.UNKNOWN;
  }
}

function determineGoogleAuthErrorCategory(errorCode: string): AuthErrorCategory {
  // Firebase Google auth error codes
  switch (errorCode) {
    case 'auth/popup-blocked':
      return AuthErrorCategory.POPUP_BLOCKED;
    case 'auth/popup-closed-by-user':
      return AuthErrorCategory.POPUP_CLOSED;
    case 'auth/cancelled-popup-request':
      return AuthErrorCategory.CANCELLED;
    case 'auth/network-request-failed':
      return AuthErrorCategory.NETWORK;
    case 'auth/unauthorized-domain':
    case 'auth/operation-not-allowed':
      return AuthErrorCategory.PERMISSION;
    case 'auth/account-exists-with-different-credential':
      return AuthErrorCategory.ACCOUNT_EXISTS;
    case 'auth/expired-action-code':
    case 'auth/invalid-action-code':
      return AuthErrorCategory.TOKEN_EXPIRED;
    default:
      return AuthErrorCategory.UNKNOWN;
  }
}

export { actionCodeSettings, saveSessionToFirestore, loginWithEmailLink, loginWithGoogle, AuthErrorCategory };